// deps
import React, { ReactElement, useMemo, useCallback, ReactNode, memo, useContext, useRef } from "react";

// internal-deps
import { CheckoutRoute, useNavigate, UserContext, AnalyticsContext } from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { LinkAccountPhoneNumber } from "@swiggy-private/payment-ui";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// components
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper";

//hooks
import { useGetWalletDetails } from "@payment-client/hooks/UseGetWalletData";

//helpers
import { getLinkWalletPageTitle } from "@payment-client/helpers/WalletHelper";

//analytics
import {
    clickProceedLinkWalletEvent,
    linkAccountWithNumberScreenViewEvent,
} from "@payment-client/analytics/events/LinkWallet";
import { CONTEXT } from "@payment-client/analytics/constants";

//constants
import { WALLET_DISPLAY_NAMES } from "@payment-client/constants/Wallet";

//context
import { LinkWalletContext } from "@payment-client/contexts/LinkWalletContext";

interface PaymentLinkWalletProps
    extends RouteProps<{
        walletCode: string;
        isOldWalletLinkFlow: boolean;
    }> {
    isPaymentModeRoute?: boolean;
}

const LinkWalletWithNumberWrapper = function VerifyWalletLinkOtpWrapper(props: {
    children: ReactNode;
    title: string;
    isPaymentModeRoute: boolean;
}): ReactElement {
    const { title, children, isPaymentModeRoute } = props;

    if (isPaymentModeRoute) {
        return <PaymentModesWrapper title={title}>{children}</PaymentModesWrapper>;
    }

    const header = <CommonHeaderContent title={title} />;
    return <CommonUIWrapper header={header} content={children} />;
};

function LinkWalletWithNumber({ data, isPaymentModeRoute = false }: PaymentLinkWalletProps): ReactElement | null {
    const navigateTo = useNavigate();
    const walletCode = data?.walletCode as WALLET_CODES;
    const isOldWalletLinkFlow = !!data?.isOldWalletLinkFlow;
    const { user } = useContext(UserContext);
    const registeredMobileNumber = user.map(user => user.mobile);
    const { clickEvent, screenViewEvent } = useContext(AnalyticsContext);
    const { linkWalletState, setLinkWalletState } = useContext(LinkWalletContext);
    const navigatingToVerifyLinkWalletOtp = useRef(false);

    const { walletDetails } = useGetWalletDetails(walletCode, isPaymentModeRoute);

    const setMobileNumber = useCallback(
        (mobileNumber: string) => {
            setLinkWalletState({ mobileNumber });
        },
        [setLinkWalletState],
    );
    useEffectOnce(() => {
        return (): void => {
            if (!navigatingToVerifyLinkWalletOtp.current) {
                setLinkWalletState({ mobileNumber: "" });
            }
        };
    });

    const onConfirmLinking = useCallback(
        async (mobileNumber: string) => {
            const navigationRoute = isPaymentModeRoute
                ? CheckoutRoute.PAYMENT_MODES_VERIFY_WALLET_LINK_OTP
                : CheckoutRoute.VERIFY_WALLET_LINK_OTP;
            const context = isPaymentModeRoute
                ? CONTEXT.LINK_WALLET_SOURCE_ACCOUNT
                : CONTEXT.LINK_WALLET_SOURCE_PAYMENT;
            clickProceedLinkWalletEvent(walletCode, context, clickEvent);
            navigatingToVerifyLinkWalletOtp.current = true;
            navigateTo(navigationRoute, {
                walletCode,
                mobileNumber,
                iconUrl: walletDetails?.iconUrl,
                paymentCode: walletDetails?.paymentCode,
            });
        },
        [clickEvent, isPaymentModeRoute, navigateTo, walletCode, walletDetails],
    );

    const title = useMemo(() => getLinkWalletPageTitle(walletCode), [walletCode]);

    const editableState = useMemo(() => {
        return {
            isEditable: !isOldWalletLinkFlow,
            registeredMobileNumber: registeredMobileNumber.unwrapOr(""),
        };
    }, [isOldWalletLinkFlow, registeredMobileNumber]);

    useEffectOnce(() => {
        const linkWalletWithNumberSource = isPaymentModeRoute
            ? CONTEXT.LINK_WALLET_SOURCE_ACCOUNT
            : CONTEXT.LINK_WALLET_SOURCE_PAYMENT;

        linkAccountWithNumberScreenViewEvent(
            walletCode,
            linkWalletWithNumberSource,
            !isOldWalletLinkFlow,
            screenViewEvent,
        );
    });

    return (
        <LinkWalletWithNumberWrapper title={title} isPaymentModeRoute={isPaymentModeRoute}>
            <LinkAccountPhoneNumber
                onConfirmLinking={onConfirmLinking}
                iconUrl={walletDetails?.iconUrl || ""}
                displayName={WALLET_DISPLAY_NAMES[walletCode]}
                editableState={editableState}
                mobileNumber={linkWalletState.mobileNumber}
                setMobileNumber={setMobileNumber}
            />
        </LinkWalletWithNumberWrapper>
    );
}

export default memo(LinkWalletWithNumber);
